import { api, ApiError } from './api'
import type {
  ArrearsAgingSummary,
  ArrearsBucket,
  BillingCycle,
  GenerationRun,
  Invoice,
  InvoiceMeterReading,
  InvoiceStatus,
  InvoiceType,
  MemberArrears,
  MemberBalance,
  PaginationMeta,
} from '@riverpark/types'

export interface GenerateInvoicesInput {
  utilityTypeId: string
  billingPeriod: string
}

export async function generateInvoices(
  input: GenerateInvoicesInput,
): Promise<{ data: GenerationRun }> {
  return api.post<{ data: GenerationRun }>('/billing/generate', input)
}

export interface InvoiceFilters {
  page?: number
  limit?: number
  status?: InvoiceStatus
  utilityTypeId?: string
  memberId?: string
  invoiceType?: InvoiceType
  excludeInvoiceType?: InvoiceType
  billingPeriod?: string
}

export async function listInvoices(
  filters: InvoiceFilters = {},
): Promise<{ data: Invoice[]; meta: PaginationMeta }> {
  const params = new URLSearchParams()
  if (filters.page) params.set('page', String(filters.page))
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.status) params.set('status', filters.status)
  if (filters.utilityTypeId) params.set('utilityTypeId', filters.utilityTypeId)
  if (filters.memberId) params.set('memberId', filters.memberId)
  if (filters.invoiceType) params.set('invoiceType', filters.invoiceType)
  if (filters.excludeInvoiceType) params.set('excludeInvoiceType', filters.excludeInvoiceType)
  if (filters.billingPeriod) params.set('billingPeriod', filters.billingPeriod)
  const query = params.toString()
  return api.get<{ data: Invoice[]; meta: PaginationMeta }>(
    `/billing/invoices${query ? `?${query}` : ''}`,
  )
}

export async function getInvoice(id: string): Promise<{ data: Invoice }> {
  return api.get<{ data: Invoice }>(`/billing/invoices/${id}`)
}

export async function getInvoiceReading(id: string): Promise<{ data: InvoiceMeterReading | null }> {
  return api.get<{ data: InvoiceMeterReading | null }>(`/billing/invoices/${id}/reading`)
}

const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'

/** Fetches the invoice PDF from the API and triggers a client-side save — mirrors
 * downloadEstateReport() in lib/payments.ts, since the API lives on a separate subdomain and a
 * plain <a href> would bypass the auth cookie. */
export async function downloadInvoicePdf(invoice: Invoice): Promise<void> {
  const res = await fetch(`${API_BASE}/billing/invoices/${invoice.id}/pdf`, {
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to generate invoice PDF')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${invoice.invoiceNumber}.pdf`
  anchor.click()
  URL.revokeObjectURL(url)
}

/** Fetches the reading photo behind an invoice as a blob — same reason as downloadInvoicePdf
 * above (separate-subdomain API, cookie auth), but for display (an inline <img>) rather than a
 * client-side save. Caller owns the object URL's lifetime (revoke it on unmount). */
export async function fetchInvoiceReadingPhoto(invoiceId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/billing/invoices/${invoiceId}/reading-photo`, {
    credentials: 'include',
  })
  if (!res.ok) throw new ApiError(res.status, 'Failed to load reading photo')
  return res.blob()
}

/** The caller's full account statement — invoices, credit notes, and payments combined into one
 * running-balance ledger — as one PDF. Same download mechanics as downloadInvoicePdf above. */
export async function downloadMemberStatement(): Promise<void> {
  const res = await fetch(`${API_BASE}/billing/statement`, { credentials: 'include' })
  if (!res.ok) throw new ApiError(res.status, 'Failed to generate statement')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `riverpark-statement-${new Date().toISOString().slice(0, 10)}.pdf`
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function markInvoiceAsSent(id: string): Promise<{ data: Invoice }> {
  return api.patch<{ data: Invoice }>(`/billing/invoices/${id}/mark-sent`, {})
}

export interface MarkDraftInvoicesAsSentInput {
  utilityTypeId?: string
}

export async function markDraftInvoicesAsSent(
  input: MarkDraftInvoicesAsSentInput = {},
): Promise<{ data: { updated: number } }> {
  return api.post<{ data: { updated: number } }>('/billing/invoices/mark-sent', input)
}

export interface MemberInvoiceFilters {
  invoiceType?: InvoiceType
  excludeInvoiceType?: InvoiceType
}

/** Member's own invoices — the API returns everything for a member (no pagination), optionally
 * narrowed by invoice type. Always sends mine=true: a committee/estate_manager account calling
 * this from their own self-service page must get their own invoices, not the staff-wide list
 * their role would otherwise return. */
export async function listMemberInvoices(
  filters: MemberInvoiceFilters = {},
): Promise<{ data: Invoice[] }> {
  const params = new URLSearchParams({ mine: 'true' })
  if (filters.invoiceType) params.set('invoiceType', filters.invoiceType)
  if (filters.excludeInvoiceType) params.set('excludeInvoiceType', filters.excludeInvoiceType)
  return api.get<{ data: Invoice[] }>(`/billing/invoices?${params.toString()}`)
}

export interface ArrearsParams {
  page?: number
  limit?: number
  bucket?: ArrearsBucket
}

export async function listMemberArrears(
  params: ArrearsParams = {},
): Promise<{ data: MemberArrears[]; meta: PaginationMeta }> {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.bucket) qs.set('bucket', params.bucket)
  const query = qs.toString()
  return api.get<{ data: MemberArrears[]; meta: PaginationMeta }>(
    `/billing/arrears${query ? `?${query}` : ''}`,
  )
}

export async function exportMemberArrears(
  bucket?: ArrearsBucket,
): Promise<{ data: MemberArrears[] }> {
  const query = bucket ? `?bucket=${bucket}` : ''
  return api.get<{ data: MemberArrears[] }>(`/billing/arrears/export${query}`)
}

export async function getArrearsAgingSummary(): Promise<{ data: ArrearsAgingSummary }> {
  return api.get<{ data: ArrearsAgingSummary }>('/billing/arrears/aging-summary')
}

export async function listGenerationRuns(): Promise<{ data: GenerationRun[] }> {
  return api.get<{ data: GenerationRun[] }>('/billing/runs')
}

export async function listBillingCycles(): Promise<{ data: BillingCycle[] }> {
  return api.get<{ data: BillingCycle[] }>('/billing/cycles')
}

export interface CreateBillingCycleInput {
  utilityTypeId: string
  billingPeriod: string
}

export async function createBillingCycle(
  input: CreateBillingCycleInput,
): Promise<{ data: BillingCycle }> {
  return api.post<{ data: BillingCycle }>('/billing/cycles', input)
}

/** `reason` is required when the caller is estate_manager — the void is then queued for
 * committee/admin approval instead of executing immediately. */
export async function voidInvoice(id: string, reason?: string): Promise<{ data: Invoice }> {
  return api.post<{ data: Invoice }>(`/billing/invoices/${id}/void`, { reason })
}

export interface CreateInvoiceAdjustmentInput {
  amount: number
  notes?: string
  /** Required when the caller is estate_manager — queues for committee/admin approval instead. */
  reason?: string
}

export async function createInvoiceAdjustment(
  invoiceId: string,
  input: CreateInvoiceAdjustmentInput,
): Promise<{ data: Invoice }> {
  return api.post<{ data: Invoice }>(`/billing/invoices/${invoiceId}/adjustment`, input)
}

export async function getMemberBalance(memberId?: string): Promise<{ data: MemberBalance }> {
  return api.get<{ data: MemberBalance }>(
    memberId ? `/billing/members/${memberId}/balance` : '/billing/balance',
  )
}
